// Open House — the page's draft state and what each save sends: today's
// draft payload (PATCH /post/drafts/:id) and the Open House answers (PATCH
// /post/drafts/:id/open_house), saved one after the other in one queued save.
// The draft holds one service (book writing, sample, editing or a bundle) in
// `posting`; a bundle's posting also holds its bundle and cover answers.

import { mergeDefaults, resolveOptions } from "@/lib/post/fieldValidation"
import { BUNDLE_COMPOSITION, bundleQueue } from "@/lib/post/bundle"
import { hydrateFromDraft, serializeBundleForSave, serializeForSave } from "@/lib/post/serialize"
import { SERVICE_CONFIGS } from "@/lib/post/services"
import { saveDraft } from "@/lib/postProjectActions"
import { saveOpenHouse } from "./actions"
import { STAGE } from "./bookWritingSteps"
import { bundleState } from "./bundleSteps"
import { isPendingFile } from "./editingSteps"
import { SERVICES, serviceOf } from "./services"

// Today's project-name fallback (PostProject's "Untitled project"). The new
// flow asks no project name, so the draft is named after its title or topic.
const UNTITLED = "Untitled project"
export const projectNameFor = (posting) => (posting.title || "").trim() || UNTITLED

// Today's POST /post/drafts body: the service, and for a bundle its tier
// (none yet is fine; a later save with a tier respawns the bundle's postings).
export function createParams(state) {
  const posting = { project_name: projectNameFor(state.posting), [state.service]: true }
  if (state.service === "bundle") posting.bundle_tier = state.posting.bundle_tier ?? null
  return { project_posting: posting }
}

// `requested` is the service a new page starts on; a resumed draft keeps its own.
export function initialState(catalog, openHouse, requested) {
  const draft = catalog?.draft
  const hydrated = draft ? hydrateFromDraft(draft, SERVICE_CONFIGS) : null
  const cursor = draft?.currentStepApi || {}
  const resumed = hydrated?.bundle ? "bundle" : hydrated?.selected.find((key) => Object.hasOwn(SERVICES, key))
  const service = serviceOf(hydrated ? resumed : requested)
  const saved = hydrated && (service.fromDraft ? service.fromDraft(hydrated) : hydrated.postings[service.key])

  return {
    draftId: draft?.id ?? null,
    service: service.key,
    posting: mergeDefaults(service.defaults, saved || {}),
    oh: {
      short_description: openHouse?.shortDescription ?? "",
      outline_text: openHouse?.outlineText ?? "",
      supplies_outline: openHouse?.suppliesOutline ?? null,
      // "paste" | "upload" | null (not chosen): the only outline source that counts.
      outline_source: openHouse?.outlineSource ?? null,
      outlineFile: openHouse?.outlineFile ?? null,
      // A chosen outline File not yet saved; a removal not yet saved (a token,
      // so a save only clears the removal it carried).
      pendingFile: null,
      removeFile: null,
    },
    stepIndex: cursor.stage === STAGE ? cursor.stepIndex ?? 0 : 0,
  }
}

// Today's draft payload, from today's serializer and Book Writing config, so the
// same columns and composites (AI consent, sensitive topics) are written.
// Today's serializer leaves a hidden answer out of the save, so the posting
// would keep (and publish) an answer the customer can no longer see: a niche
// after switching a sample to fiction, US/UK English after choosing
// Developmental Editing. The new flow saves every answer its steps hide as
// cleared instead, and a switched-off toggle or sensitive-topic warning
// without its details. Answers the page shows always win (draftPayload).
const CLEARABLE = new Set(["select", "radio", "text", "textarea"])
const CONSENT_KEYS = [
  "ai_consent_none", "ai_consent_research", "ai_consent_all", "ai_consent_adjust",
  "ai_consent_generate", "ai_consent_reduce", "ai_consent_structuring",
]

const booleanChoice = (f, values, ctx) =>
  f.type === "radio" && resolveOptions(f, values, ctx).every((option) => typeof option.value === "boolean")

export function hiddenClears(steps, values, ctx) {
  const fields = steps.flatMap((step) => step.fields)
  const isHidden = (f) => !!f.when && !f.when(values, ctx)
  const shown = new Set(fields.filter((f) => !isHidden(f)).map((f) => f.key))
  const clears = {}

  for (const f of fields) {
    const hidden = isHidden(f)
    if (f.type === "triggerWarning") {
      if (hidden || !values.trigger_warning) Object.assign(clears, { trigger_warning: false, trigger_warning_details: null })
    } else if (f.type === "consent") {
      if (hidden) for (const key of CONSENT_KEYS) clears[key] = false
    } else if (f.type === "toggle") {
      if (hidden) clears[f.key] = false
      if (f.detailsKey && (hidden || !values[f.key])) clears[f.detailsKey] = null
    } else if (hidden && f.key && !shown.has(f.key) && CLEARABLE.has(f.type)) {
      clears[f.key] = booleanChoice(f, values, ctx) ? false : null
    }
  }
  return clears
}

// A bundle saves through today's bundle serializer: the manuscript and cover
// postings, plus the outline and narration children its tier includes. Only
// the manuscript's hidden answers are cleared; the bundle step hides none.
export function draftPayload(snapshot, ctx) {
  const service = snapshot.service
  const bundle = service === "bundle"
  const steps = serviceOf(service).steps(snapshot.posting, ctx)
  const index = Math.min(snapshot.stepIndex, steps.length - 1)
  const wizard = {
    projectName: projectNameFor(snapshot.posting),
    bundle,
    selected: bundle ? bundleQueue() : [service],
    postings: { [service]: snapshot.posting },
    ...(bundle ? bundleState(snapshot.posting) : {}),
    stage: STAGE,
    queueIndex: 0,
    stepIndex: index,
    managed_services_tier: "self_managed",
    quality_assurance_addon: "declined",
    quality_control_enabled: "declined",
  }
  const stamp = { stepLabel: steps[index].node, stepNum: index + 1, stepTotal: steps.length }

  const config = SERVICE_CONFIGS[bundle ? "book_writing" : service]
  const payload = bundle
    ? serializeBundleForSave(wizard, SERVICE_CONFIGS, ctx, stamp)
    : serializeForSave(wizard, { [service]: config }, ctx, stamp)
  const key = `${config.postingKey}_attributes`
  const attrs = payload.project_posting[key]
  const manuscript = bundle ? wizard.postings.book_writing : snapshot.posting
  payload.project_posting[key] = { ...hiddenClears(steps.filter((s) => s.id !== "bundle"), manuscript, ctx), ...attrs }
  return payload
}

// The posting's own file a draft save carries (editing's manuscript): today's
// `file` on the posting, a File until the save that sent it succeeds.
const postingAttributes = (draft) =>
  Object.entries(draft?.project_posting || {}).find(([key]) => key.endsWith("_posting_attributes"))

export function postingFile(draft) {
  const file = postingAttributes(draft)?.[1]?.file
  return isPendingFile(file) ? file : null
}

// After the save that sent it, the posting keeps the saved file's name.
export function acknowledgePostingFile(state, file) {
  if (state.posting.file !== file) return state
  return { ...state, posting: { ...state.posting, file: { name: file.name } } }
}

// A move to a bundle that includes an outline drops the customer's own outline
// (choice, source, pasted text, file), as the server does in that save
// (JobPostings::OpenHouseRebundle).
export function ohForTier(oh, tier) {
  if (!BUNDLE_COMPOSITION[tier]?.outline) return oh
  return { ...oh, supplies_outline: null, outline_source: null, outline_text: "", outlineFile: null, pendingFile: null }
}

// One answer applied to the page's state, with today's cascade: fiction is
// always human-written, and an AI-generated manuscript brings its own outline.
export function answerUpdate(prev, key, value) {
  if (key === "short_description" || key === "outline_text") return { ...prev, oh: { ...prev.oh, [key]: value } }
  if (key === "outline_choice") {
    return {
      ...prev,
      posting: { ...prev.posting, add_outline: value === "paid" },
      oh: { ...prev.oh, supplies_outline: value === "own" },
    }
  }
  const next = { ...prev.posting, [key]: value }
  if (key === "fiction" && value !== prev.posting.fiction) next.use_ai = false
  if (key === "use_ai" && value) next.add_outline = false
  const service = serviceOf(prev.service)
  const oh = key === "bundle_tier" ? ohForTier(prev.oh, value) : prev.oh
  return { ...prev, posting: service.cascade ? service.cascade(next, key, value) : next, oh }
}

// The outline choice and source are always sent, so a cleared one saves as cleared.
export function openHousePayload(oh) {
  const answers = {
    short_description: oh.short_description, outline_text: oh.outline_text,
    supplies_outline: oh.supplies_outline ?? null, outline_source: oh.outline_source || null,
  }
  if (oh.removeFile) answers.remove_outline_file = true
  if (oh.pendingFile) answers.outline_file = oh.pendingFile
  return answers
}

// A save queued behind the one that uploaded its outline file or manuscript,
// or that sent its outline removal (`payload.removal` is the removal's token),
// sends neither again: a second removal would detach the file uploaded after
// the first.
export function withoutUploaded(payload, uploaded, removed) {
  const { outline_file: file, remove_outline_file: removal, ...rest } = payload.openHouse
  const answers = { ...rest }
  if (file && !uploaded.has(file)) answers.outline_file = file
  if (removal && !removed.has(payload.removal)) answers.remove_outline_file = removal

  const manuscript = postingFile(payload.draft)
  if (!manuscript || !uploaded.has(manuscript)) return { ...payload, openHouse: answers }

  const [key, attrs] = postingAttributes(payload.draft)
  const { file: _sent, ...kept } = attrs
  const draft = { project_posting: { ...payload.draft.project_posting, [key]: kept } }
  return { ...payload, draft, openHouse: answers }
}

// The answers (a removal included) save before the outline file, in their own
// request, so a file the server refuses never cancels them.
export async function persist(draftId, payload) {
  const draft = await saveDraft(draftId, payload.draft)
  const { outline_file: file, ...answers } = payload.openHouse
  await saveOpenHouse(draftId, answers)
  if (file) await saveOpenHouse(draftId, { outline_file: file })
  return draft
}
